import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { ArrowRight, Clock, Target, Zap, Brain, Sparkles, ShieldCheck } from 'lucide-react';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer } from 'recharts';

const HERO_RADAR = [
  { subject: 'Memory', score: 87 },
  { subject: 'Recall', score: 84 },
  { subject: 'Speed', score: 92 },
  { subject: 'Pattern', score: 78 },
  { subject: 'Focus', score: 86 }
];

export default function HomePage() {
  const { user } = useAuth();

  const handleStart = () => {
    if (!user) {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const redirectUrl = window.location.origin + '/dashboard';
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    }
  };

  return (
    <div className="min-h-screen" data-testid="homepage">
      {/* Hero — black, OG-aligned */}
      <section className="border-b border-[#09090B] bg-[#09090B] text-white relative overflow-hidden" data-testid="hero-section">
        <div className="container-swiss">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 items-center">
            {/* Left: Text */}
            <div className="lg:col-span-7 py-16 md:py-24 lg:py-32 lg:pr-12 flex flex-col justify-center animate-fade-in">
              <p className="mono-data text-zinc-400 mb-6" data-testid="hero-label">
                Cognitive Training Platform
              </p>
              <h1
                className="text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] mb-8"
                style={{ fontFamily: 'Outfit, sans-serif' }}
                data-testid="hero-heading"
              >
                Measure<br />
                the brain.<br />
                <span className="text-white">Train it.</span>
              </h1>
              <p className="text-lg md:text-xl text-zinc-300 leading-relaxed mb-10 max-w-xl">
                Cognitive training in structured 1-hour sessions. Five dimensions, one
                BrainMap Score (0-900) you can actually track. Built for Gen Z and the parents
                serious about measurable growth.
              </p>
              <div className="flex flex-wrap gap-4">
                {user ? (
                  <Link
                    to="/dashboard"
                    className="bg-[#09090B] text-white px-8 py-4 text-sm font-bold tracking-widest uppercase hover:bg-zinc-800 transition-colors flex items-center gap-3"
                    data-testid="hero-cta-dashboard"
                  >
                    Go to Dashboard <ArrowRight className="w-4 h-4" />
                  </Link>
                ) : (
                  <button
                    onClick={handleStart}
                    className="bg-[#09090B] text-white px-8 py-4 text-sm font-bold tracking-widest uppercase hover:bg-zinc-800 transition-colors flex items-center gap-3"
                    data-testid="hero-cta-start"
                  >
                    Start your first session <ArrowRight className="w-4 h-4" />
                  </button>
                )}
                <Link
                  to="/programs"
                  className="border border-zinc-600 text-white px-8 py-4 text-sm font-bold tracking-widest uppercase hover:bg-white hover:text-black transition-colors flex items-center gap-3"
                  data-testid="hero-cta-browse"
                >
                  Browse Programs
                </Link>
              </div>
            </div>

            {/* Right: Live BrainMap radar */}
            <div className="hidden lg:flex lg:col-span-5 items-center justify-center py-16">
              <div className="relative" data-testid="hero-radar">
                <ResponsiveContainer width={440} height={440}>
                  <RadarChart data={HERO_RADAR}>
                    <PolarGrid stroke="#27272a" />
                    <PolarAngleAxis dataKey="subject" tick={{ fill: '#a1a1aa', fontSize: 12, fontFamily: 'JetBrains Mono' }} />
                    <PolarRadiusAxis angle={90} domain={[0, 100]} tick={false} stroke="#27272a" />
                    <Radar dataKey="score" stroke="#FFFFFF" fill="#FFFFFF" fillOpacity={0.18} strokeWidth={2.5} />
                  </RadarChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="text-center bg-[#09090B] px-4 py-2">
                    <div className="text-5xl font-black tracking-tighter text-white" style={{ fontFamily: 'JetBrains Mono, monospace' }}>763</div>
                    <div className="mono-data text-[10px] text-zinc-500 mt-1">/ 900 BRAINMAP</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="border-b border-[#09090B]" data-testid="features-section">
        <div className="container-swiss">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-l border-[#09090B]">
            {[
              {
                icon: Brain,
                title: 'Memory',
                desc: 'Encode, store, and retrieve information with precision. Techniques from memory champions, applied systematically.',
                stat: '20 words in 1 session'
              },
              {
                icon: Target,
                title: 'Focus',
                desc: 'Train sustained attention and eliminate cognitive noise. Progressive exercises that measurably extend your focus span.',
                stat: '1-hours of flow'
              },
              {
                icon: Zap,
                title: 'Thinking',
                desc: 'Creative problem-solving and critical analysis. Break conventional patterns and generate novel solutions on demand.',
                stat: '5 cognitive domains'
              }
            ].map((f, i) => (
              <div
                key={f.title}
                className={`border-r border-b border-[#09090B] p-8 md:p-12 animate-slide-up stagger-${i + 1}`}
                style={{ animationFillMode: 'backwards' }}
                data-testid={`feature-${f.title.toLowerCase()}`}
              >
                <f.icon className="w-6 h-6 text-[#09090B] mb-6" />
                <h3 className="text-2xl font-bold tracking-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {f.title}
                </h3>
                <p className="text-zinc-600 leading-relaxed mb-6">{f.desc}</p>
                <p className="mono-data text-[#09090B]">{f.stat}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-24 md:py-32 border-b border-[#09090B]" data-testid="how-it-works">
        <div className="container-swiss">
          <p className="mono-data text-[#09090B] mb-4">How It Works</p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-16" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Program &rarr; Session &rarr; Result
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                title: 'Choose a Program',
                desc: 'Browse 25+ programs across Memory, Focus, Career, Communication, and Behavior categories.'
              },
              {
                step: '02',
                title: 'Complete Sessions',
                desc: 'Each program contains structured 1-hour sessions. No passive watching \u2014 continuous interaction.'
              },
              {
                step: '03',
                title: 'Track Progress',
                desc: 'See your scores improve session by session. Real metrics, real improvement, real results.'
              }
            ].map((item) => (
              <div key={item.step} className="border border-[#09090B] p-8" data-testid={`step-${item.step}`}>
                <span className="mono-data text-[#09090B] text-2xl font-black">{item.step}</span>
                <h3 className="text-xl font-semibold tracking-tight mt-4 mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {item.title}
                </h3>
                <p className="text-zinc-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CognoGuru Safe™ — premium intelligence layer */}
      <section className="py-24 md:py-32 border-b border-[#09090B] bg-[#09090B] text-white relative overflow-hidden" data-testid="cognoguru-section">
        <div className="container-swiss relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-2 mb-6">
                <ShieldCheck className="w-4 h-4 text-white" />
                <span className="mono-data text-white">Premium Intelligence Layer</span>
              </div>
              <h2 className="text-4xl md:text-6xl font-black tracking-tighter mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                CognoGuru Safe<sup className="text-xl align-super">™</sup>
              </h2>
              <p className="text-xl md:text-2xl text-zinc-300 mb-6 font-light tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Your Expert-Guided Cognitive Intelligence Companion
              </p>
              <p className="text-base text-zinc-400 leading-relaxed max-w-xl mb-10">
                Ask safe, age-appropriate questions about focus, memory, learning, thinking, habits, emotional balance, and cognitive development — guided by expert-curated knowledge and verified sources from NIH, Harvard, Stanford, APA, and peer-reviewed neuroscience research.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/cognoguru" className="inline-flex items-center gap-3 bg-white text-[#09090B] px-6 py-4 text-xs font-bold tracking-widest uppercase hover:bg-zinc-200 transition-colors" data-testid="cognoguru-explore-cta">
                  Explore CognoGuru Safe <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/cognoguru#gurus" className="inline-flex items-center gap-3 border border-white px-6 py-4 text-xs font-bold tracking-widest uppercase hover:bg-white hover:text-[#09090B] transition-colors" data-testid="cognoguru-meet-cta">
                  Meet the 10 CognoGurus
                </Link>
              </div>
              <p className="mono-data text-[10px] text-zinc-500 mt-8 max-w-md">
                Not a generic AI chatbot. An expert-curated cognitive intelligence system, parent-trustworthy and child-safe.
              </p>
            </div>

            <div className="lg:col-span-5">
              <div className="grid grid-cols-2 gap-2">
                {[
                  { name: 'Focus', icon: Target },
                  { name: 'Memory', icon: Brain },
                  { name: 'Thinking', icon: Zap },
                  { name: 'Learning', icon: Sparkles },
                  { name: 'Calm', icon: ShieldCheck },
                  { name: 'Habit', icon: Clock },
                  { name: 'Creativity', icon: Sparkles },
                  { name: 'Social Brain', icon: Brain },
                  { name: 'Sleep', icon: Clock },
                  { name: 'Peak Brain', icon: Zap },
                ].map((g, i) => {
                  const Icon = g.icon;
                  return (
                    <div key={g.name} className="border border-zinc-700 hover:border-white p-4 transition-colors group" data-testid={`mini-guru-${i}`}>
                      <Icon className="w-4 h-4 text-zinc-400 group-hover:text-white mb-2 transition-colors" />
                      <div className="text-xs font-bold tracking-wide uppercase text-zinc-300 group-hover:text-white transition-colors">
                        {g.name}
                      </div>
                      <div className="mono-data text-[9px] text-zinc-600 mt-1">Guru</div>
                    </div>
                  );
                })}
              </div>
              <p className="mono-data text-[10px] text-zinc-500 mt-4 text-right">
                The Council of 10 →
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 border-b border-[#09090B]" data-testid="cta-section">
        <div className="container-swiss text-center">
          <div className="flex items-center justify-center gap-2 mb-6">
            <Clock className="w-5 h-5 text-[#09090B]" />
            <span className="mono-data text-[#09090B]">1 Hour</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-black tracking-tighter mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Your Brain is Waiting
          </h2>
          <p className="text-lg text-zinc-600 mb-10 max-w-xl mx-auto">
            Most people scroll for an hour. You could spend that same hour transforming your cognitive abilities.
          </p>
          {user ? (
            <Link to="/dashboard" className="btn-accent inline-flex items-center gap-3" data-testid="cta-btn">
              Go to Dashboard <ArrowRight className="w-4 h-4" />
            </Link>
          ) : (
            <button onClick={handleStart} className="btn-accent inline-flex items-center gap-3" data-testid="cta-btn">
              Begin Your 1 Hour <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#09090B] text-white py-16 md:py-24" data-testid="footer">
        <div className="container-swiss">
          <div className="flex flex-col md:flex-row justify-between items-start gap-8">
            <div>
              <h3
                className="text-3xl md:text-5xl font-black tracking-tighter mb-4"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                FOKYES
              </h3>
              <p className="text-zinc-400 max-w-sm">
                Cognitive training that delivers measurable results. One hour at a time.
              </p>
            </div>
            <div className="flex gap-8">
              <Link to="/programs" className="text-zinc-400 hover:text-white transition-colors text-sm tracking-wide uppercase">
                Programs
              </Link>
              <Link to="/pricing" className="text-zinc-400 hover:text-white transition-colors text-sm tracking-wide uppercase">
                Pricing
              </Link>
            </div>
          </div>
          <div className="border-t border-zinc-800 mt-12 pt-8 space-y-6">
            {/* Website Security disclaimer */}
            <div className="flex flex-col md:flex-row items-start md:items-center gap-3" data-testid="home-footer-security">
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="mono-data text-[10px] text-white">■ Website Security</span>
              </div>
              <p className="text-[11px] text-zinc-400 leading-relaxed max-w-3xl">
                Encrypted end-to-end over HTTPS (TLS 1.3). Personal data stored in access-controlled, region-restricted databases with regular integrity checks. We never sell user data. Children&apos;s data collection follows strict verifiable-parental-consent (VPC) rules. Report a security concern to <a href="mailto:security@storytellercharles.com" className="text-white underline hover:no-underline">security@storytellercharles.com</a>.
              </p>
            </div>

            {/* Attribution / rebrand credit */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-[11px] text-zinc-400" data-testid="home-footer-attribution">
              <div>
                <p className="mono-data text-[9px] text-zinc-600 mb-1">Original concept</p>
                <a href="https://godwinsureah.com" target="_blank" rel="noopener noreferrer" className="hover:text-white" data-testid="home-footer-original-concept">
                  godwinsureah.com →
                </a>
              </div>
              <div>
                <p className="mono-data text-[9px] text-zinc-600 mb-1">Formerly</p>
                <span data-testid="home-footer-rebrand-note">in3600seconds · rebranded to FOKYES</span>
              </div>
              <div>
                <p className="mono-data text-[9px] text-zinc-600 mb-1">Powered by</p>
                <span className="font-semibold text-white" data-testid="home-footer-powered-by">GdoubleU Foundation</span>
              </div>
            </div>

            <div className="border-t border-zinc-800 pt-4 flex flex-col md:flex-row justify-between items-start md:items-center gap-2">
              <p className="mono-data text-zinc-500 text-xs">&copy; {new Date().getFullYear()} FOKYES. All rights reserved.</p>
              <p className="mono-data text-[10px] text-zinc-600">DPDP • COPPA • GDPR-K compliant</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
